import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { ApiConnectionString } from "../../../constants/ApiConntection";

export default function Enquiries({ profile,user }) {
  const [filter, setFilter] = useState("All");

  const [enquires, setEnquires] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [confirmArchiveId, setConfirmArchiveId] = useState(null);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${ApiConnectionString}/api/Enquires`, {
        headers: { Authorization: `bearer ${user.token}` },
      });
      if (res.status) {
        setEnquires(res.data);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (id) => {
    if (!id) return;

    try {
      await axios.get(`${ApiConnectionString}/api/Enquiry/${id}`, {
        headers: { Authorization: `bearer ${user.token}` },
      });

      setEnquires((prev) =>
        prev.map((x) => (x.id === id ? { ...x, read: true } : x))
      );
    } catch {}
  };

  const toggleArchive = async (id) => {
    if (!id) return;

    try {
      const res = await axios.put(
        `${ApiConnectionString}/api/Enquiry/${id}`,
        {},
        {
          headers: { Authorization: `bearer ${user.token}` },
        }
      );

      if (res?.data) {
        setEnquires((prev) =>
          prev.map((x) => (x.id === id ? res.data : x))
        );

        if (selectedEnquiry?.id === id) {
          setSelectedEnquiry(res.data);
        }
      }
    } catch {}

    setConfirmArchiveId(null);
  };

  useEffect(() => {
    if(user){
    fetchData();
    }

  }, [user]);

  const enquiries = useMemo(() => enquires || [], [enquires]);

  const filtered = useMemo(() => {
    if (filter === "Archived") {
      return enquiries.filter((e) => e.archived);
    }

    if (filter === "Unread") {
      return enquiries.filter((e) => !e.read && !e.archived);
    }

    if (filter === "Read") {
      return enquiries.filter((e) => e.read && !e.archived);
    }

    return enquiries.filter((e) => !e.archived);
  }, [filter, enquiries]);

  const counts = useMemo(() => {
    const c = { All: 0, Unread: 0, Read: 0, Archived: 0 };

    enquiries.forEach((e) => {
      if (e.archived) {
        c.Archived += 1;
      } else {
        c.All += 1;
        if (e.read) c.Read += 1;
        else c.Unread += 1;
      }
    });

    return c;
  }, [enquiries]);

  const name =
    (profile && (profile.name || profile.fullName || profile.displayName)) ||
    "Your profile";

  const badgeClass = (read, archived) => {
    if (archived) return "db-badge";
    if (!read) return "db-badge db-badge-purple";
    return "db-badge";
  };

  const badgeText = (read, archived) => {
    if (archived) return "Archived";
    return read ? "Read" : "Unread";
  };

  const formatCreated = (created) => {
    if (!created) return "";
    const d = new Date(created);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const confirmText = (enquiry) => {
    if (enquiry?.archived) return "Unarchive this enquiry?";
    return "Archive this enquiry?";
  };

  const truncateMessage = (message) => {
    if (!message) return "";
    const limit = 120;
    if (message.length <= limit) return message;
    return `${message.slice(0, limit)}…`;
  };

  return (
    <div className="db-card">
      <div className="db-main-head">
        <div>
          <h1 className="db-h1">Enquiries</h1>
          <div className="db-sub">
            Messages from potential clients for {name}
          </div>
        </div>

        <div className="db-actions">
          <select
            className="db-select"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setSelectedEnquiry(null);
              setConfirmArchiveId(null);
            }}
          >
            <option value="All">All ({counts.All})</option>
            <option value="Unread">Unread ({counts.Unread})</option>
            <option value="Read">Read ({counts.Read})</option>
            <option value="Archived">Archived ({counts.Archived})</option>
          </select>
        </div>
      </div>

      <div className="db-divider" />

      <div className="db-list">
        {selectedEnquiry ? (
          <div className="db-list-item">
            <div className="db-list-top">
              <div>
                <div className="db-list-title">{selectedEnquiry.name}</div>
                <div className="db-list-sub">
                  {formatCreated(selectedEnquiry.created)}
                </div>
              </div>

              <span
                className={badgeClass(
                  selectedEnquiry.read,
                  selectedEnquiry.archived
                )}
              >
                {badgeText(
                  selectedEnquiry.read,
                  selectedEnquiry.archived
                )}
              </span>
            </div>

            <div className="db-divider" />

            <div className="db-list-sub">
              {selectedEnquiry.contactPreference ? (
                <div>
                  <strong>Contact preference:</strong>{" "}
                  {selectedEnquiry.contactPreference}
                </div>
              ) : null}

              {selectedEnquiry.email ? (
                <div>
                  <strong>Email:</strong> {selectedEnquiry.email}
                </div>
              ) : null}

              {selectedEnquiry.phone ? (
                <div>
                  <strong>Phone:</strong> {selectedEnquiry.phone}
                </div>
              ) : null}
            </div>

            <div className="db-divider" />

            <div className="db-list-sub">{selectedEnquiry.message}</div>

            <div className="db-divider" />

            <div className="db-actions">
              <button
                type="button"
                className="db-btn db-btn-ghost"
                onClick={() => {
                  setSelectedEnquiry(null);
                  setConfirmArchiveId(null);
                }}
              >
                Back
              </button>

              {confirmArchiveId === selectedEnquiry.id ? (
                <>
                  <div className="db-list-sub">
                    {confirmText(selectedEnquiry)}
                  </div>

                  <button
                    type="button"
                    className="db-btn db-btn-primary"
                    onClick={() => toggleArchive(selectedEnquiry.id)}
                  >
                    Confirm
                  </button>

                  <button
                    type="button"
                    className="db-btn db-btn-ghost"
                    onClick={() => setConfirmArchiveId(null)}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="db-btn db-btn-ghost"
                  onClick={() => setConfirmArchiveId(selectedEnquiry.id)}
                >
                  {selectedEnquiry.archived ? "Unarchive" : "Archive"}
                </button>
              )}
            </div>
          </div>
        ) : isLoading ? (
          <div className="db-list-item">
            <div className="db-list-title">Loading enquiries</div>
            <div className="db-list-sub">Please wait</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="db-list-item">
            <div className="db-list-title">No enquiries</div>
            <div className="db-list-sub">
              Nothing matches this filter right now
            </div>
          </div>
        ) : (
          filtered.map((e) => (
            <div key={e.id} className="db-list-item">
              <div className="db-list-top">
                <div>
                  <div className="db-list-title">{e.name}</div>
                  <div className="db-list-sub">
                    {formatCreated(e.created)}
                    {e.contactPreference ? ` · ${e.contactPreference}` : ""}
                    {e.email ? ` · ${e.email}` : ""}
                    {e.phone ? ` · ${e.phone}` : ""}
                  </div>
                </div>

                <span className={badgeClass(e.read, e.archived)}>
                  {badgeText(e.read, e.archived)}
                </span>
              </div>

              <div className="db-divider" />

              <div className="db-list-sub">{truncateMessage(e.message)}</div>

              <div className="db-divider" />

              <div className="db-actions">
                <button
                  type="button"
                  className="db-btn db-btn-primary"
                  onClick={() => {
                    setSelectedEnquiry(e);
                    setConfirmArchiveId(null);
                    if (!e.read) {
                      markAsRead(e.id);
                    }
                  }}
                >
                  View
                </button>

                {confirmArchiveId === e.id ? (
                  <>
                    <div className="db-list-sub">{confirmText(e)}</div>

                    <button
                      type="button"
                      className="db-btn db-btn-ghost"
                      onClick={() => toggleArchive(e.id)}
                    >
                      Confirm
                    </button>

                    <button
                      type="button"
                      className="db-btn db-btn-ghost"
                      onClick={() => setConfirmArchiveId(null)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="db-btn db-btn-ghost"
                    onClick={() => setConfirmArchiveId(e.id)}
                  >
                    {e.archived ? "Unarchive" : "Archive"}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
